import { normalizeExtractedDataSources } from '@common/submission-data/submission-extracted-data/utils';
import { SubmissionExtractedData } from '@common/types';
import { convertCoverageLinesTextToEnum, convertIndustryObjectToOption } from '@common/utils';
import { normalizeAddressExtraction } from '@common/utils/extraction-utils';
import { useMemo } from 'react';
import * as yup from 'yup';
import { CoverageLine, PromptResultFeature, PromptResultFeatureType } from 'enums';
import { ExtractedDataFields, SuggestedValueProps } from 'hooks';
import { messages } from 'i18n';
import { CarrierSubmission, Suggestion } from 'types';
import { extractValidCoverageLinesSuggestion } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/EditSubmissionNew/Screens/PreForm/extracted-coverage-lines-utils';
import { getValidDateSuggestion } from 'broker/utils';

const { requiredField, invalidEmail } = messages.formMessages;

function buildSimpleSuggestion(
  suggestion: Suggestion,
  promptResultFeature: PromptResultFeatureType,
): SuggestedValueProps {
  return {
    suggestion,
    promptResultFeature,
  };
}

export function buildDefaultValues(carrierSubmission: CarrierSubmission) {
  const { submission } = carrierSubmission;
  return {
    agentName: carrierSubmission.agentName ?? '',
    insuredName: submission.insuredName ?? '',
    industry: submission.industry ? convertIndustryObjectToOption(submission.industry) : null,
    insuredMailingAddress: {
      line1: submission.insuredMailingAddress?.line1 ?? '',
      line2: submission.insuredMailingAddress?.line2 ?? '',
      city: submission.insuredMailingAddress?.city ?? '',
      state: submission.insuredMailingAddress?.state ?? '',
      postalCode: submission.insuredMailingAddress?.postalCode ?? '',
      country: submission.insuredMailingAddress?.country ?? '',
    },
    coverageLines: submission.coverageLines ?? [],
    dueDate: submission.dueDate ? new Date(submission.dueDate).toISOString() : '',
    sic: carrierSubmission.sic ?? '',
    natureOfBusiness: carrierSubmission.natureOfBusiness ?? '',
    fein: carrierSubmission.fein ?? '',
    businessEmail: carrierSubmission.businessEmail ?? '',
  };
}

export type CarrierSubmissionFormValues = ReturnType<typeof buildDefaultValues>;

export const carrierSubmissionFormSchema = yup.object().shape({
  insuredName: yup.string().default(''),
  agentName: yup.string().default(''),
  industry: yup
    .object()
    .shape({
      value: yup.string(),
      label: yup.string(),
      heraldId: yup.string(),
      naicsCode: yup.string(),
      description: yup.string(),
    })
    .nullable(),
  coverageLines: yup.array().default([]).min(1, requiredField),
  dueDate: yup.string().default(''),
  insuredMailingAddress: yup.object().shape({
    line1: yup.string().default(''),
    line2: yup.string().default(''),
    city: yup.string().default(''),
    state: yup.string().default(''),
    postalCode: yup.string().default(''),
    country: yup.string().default(''),
  }),
  sic: yup.string().default(''),
  natureOfBusiness: yup.string().default(''),
  fein: yup.string().default(''),
  businessEmail: yup.string().trim().email(invalidEmail),
});

export function useCarrierSubmissionExtractedData(
  submissionExtractedData?: SubmissionExtractedData,
): ExtractedDataFields | undefined {
  return useMemo(() => {
    const extractedData = submissionExtractedData?.extractedData;
    if (!extractedData) {
      return undefined;
    }

    const normalized = normalizeExtractedDataSources(extractedData);

    const suggestions: Record<string, SuggestedValueProps | undefined> = {};

    if (normalized.insuredName?.value) {
      suggestions.insuredName = buildSimpleSuggestion(
        normalized.insuredName,
        PromptResultFeature.CarrierSubmissionInsuredName,
      );
    }

    if (normalized.industry?.value?.mappedIndexEntry?.[0]?.heraldId) {
      const entry = normalized.industry.value.mappedIndexEntry[0];
      suggestions.industry = buildSimpleSuggestion(
        {
          value: convertIndustryObjectToOption({
            heraldId: entry.heraldId,
            naicsCode: entry.naicsCode,
            description: entry.indexEntryDescription,
          }),
          reason: normalized.industry.reason,
          citations: normalized.industry.citations,
        },
        PromptResultFeature.CarrierSubmissionIndustryClassification,
      );
    }

    if (normalized.address) {
      const normalizedAddress = normalizeAddressExtraction(normalized.address);
      const mailingAddress = normalizedAddress?.value?.mailing;
      if (mailingAddress) {
        suggestions.insuredMailingAddress = {
          suggestion: {
            value: {
              line1: mailingAddress.line1 ?? '',
              line2: mailingAddress.line2 ?? '',
              city: mailingAddress.city ?? '',
              state: mailingAddress.state ?? '',
              postalCode: mailingAddress.postal_code ?? '',
              country: mailingAddress.country_code ?? '',
            },
            reason: normalizedAddress!.reason,
            citations: normalizedAddress!.citations,
          },
          promptResultFeature: PromptResultFeature.CarrierSubmissionApplicantAddress,
          isFormFieldEmpty: (formValue: Record<string, string>) => Object.values(formValue).every((v) => !v),
        };
      }
    }

    if (extractedData.coverageLines?.value) {
      const enumValues = convertCoverageLinesTextToEnum(extractedData.coverageLines.value);
      if (enumValues.length > 0) {
        suggestions.coverageLines = extractValidCoverageLinesSuggestion(
          {
            value: enumValues,
            reason: extractedData.coverageLines.reason,
            citations: extractedData.coverageLines.citations,
          },
          Object.values(CoverageLine),
        );
      }
    }

    if (normalized.effectiveDate) {
      const validDate = getValidDateSuggestion(normalized.effectiveDate);
      if (validDate) {
        suggestions.dueDate = buildSimpleSuggestion(validDate, PromptResultFeature.CarrierSubmissionEffectiveDate);
      }
    }

    if (normalized.brokerName?.value) {
      suggestions.agentName = buildSimpleSuggestion(
        normalized.brokerName,
        PromptResultFeature.CarrierSubmissionBrokerName,
      );
    }

    if (normalized.sic?.value) {
      suggestions.sic = buildSimpleSuggestion(normalized.sic, PromptResultFeature.CarrierSubmissionSic);
    }

    if (normalized.natureOfBusiness?.value) {
      suggestions.natureOfBusiness = buildSimpleSuggestion(
        normalized.natureOfBusiness,
        PromptResultFeature.CarrierSubmissionNatureOfBusiness,
      );
    }

    if (normalized.fein?.value) {
      suggestions.fein = buildSimpleSuggestion(normalized.fein, PromptResultFeature.CarrierSubmissionFein);
    }

    if (normalized.applicantEmailAddress?.value) {
      suggestions.businessEmail = buildSimpleSuggestion(
        normalized.applicantEmailAddress,
        PromptResultFeature.SubmissionDataApplicantEmailAddress,
      );
    }

    const entries = Object.entries(suggestions).filter(([, v]) => v !== undefined);
    if (entries.length === 0) {
      return undefined;
    }

    return Object.fromEntries(entries) as ExtractedDataFields;
  }, [submissionExtractedData]);
}
